// Stefans-AI-CV-frontend/src/sections/Hero.tsx

import { motion } from 'framer-motion'
import { useI18n } from '../i18n'
import { useAppStore } from '../stores/appStore'
import { analyticsService } from '../services/analytics'

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.1 },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
}

export default function Hero() {
  const t = useI18n()
  const { currentLanguage } = useAppStore()
  const researchLink = currentLanguage === 'zh'
    ? '/research/hyperliquid-latency-deep-dive_cn.html'
    : '/research/hyperliquid-latency-deep-dive_en.html'

  const scrollToSection = (id: string, label: string) => {
    analyticsService.trackEvent('hero_cta_click', {
      category: 'hero',
      label,
      target_section: id,
      language: currentLanguage,
    })
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background grid pattern */}
      <div className="absolute inset-0 grid-pattern opacity-60" />

      {/* Floating gradient orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.06) 0%, transparent 70%)' }} />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(129,140,248,0.07) 0%, transparent 70%)' }} />
      <div className="absolute top-1/2 right-1/3 w-64 h-64 rounded-full blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.04) 0%, transparent 70%)' }} />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-24 md:py-32 w-full">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-4xl"
        >
          {/* Status label */}
          <motion.div variants={itemVariants} className="flex items-center gap-2 mb-6">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-400" />
            </span>
            <span className="text-sm font-medium text-text-secondary font-mono">Stefans</span>
          </motion.div>

          {/* Main heading */}
          <motion.div variants={itemVariants} className="mb-4">
            <p className="text-lg text-text-secondary mb-2 font-body">{t.hero.greeting}</p>
            <h1 className="font-heading font-bold leading-tight">
              <span className="text-5xl md:text-7xl text-gradient-cyan block mb-2">
                {t.hero.name}
              </span>
            </h1>
          </motion.div>

          {/* Title — primary identity */}
          <motion.div variants={itemVariants} className="mb-5">
            <p className="text-2xl md:text-3xl text-text-primary font-body leading-relaxed font-medium">
              {t.hero.title}
            </p>
          </motion.div>

          {/* Rare combo — positioning hook, right after identity */}
          <motion.p
            variants={itemVariants}
            className="text-sm text-text-secondary font-mono mb-6 border-l-2 border-cyan-400/50 pl-3"
          >
            {t.hero.rare_combo}
          </motion.p>

          {/* Subtitle — elaboration */}
          <motion.p
            variants={itemVariants}
            className="text-base md:text-lg text-text-muted leading-relaxed mb-10 max-w-2xl"
          >
            {t.hero.subtitle}
          </motion.p>

          {/* CTA buttons */}
          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-3 mb-10">
            <button
              onClick={() => scrollToSection('projects', 'view_featured_work')}
              className="px-6 py-3 rounded-xl font-medium text-sm text-bg-base bg-cyan-400 hover:bg-cyan-300 transition-all duration-200 shadow-glow-cyan hover:shadow-glow-cyan-lg"
            >
              {t.hero.cta_projects}
            </button>
            <a
              href={researchLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => analyticsService.trackEvent('hero_cta_click', {
                category: 'hero',
                label: 'read_featured_research',
                target_url: researchLink,
                language: currentLanguage,
              })}
              className="px-6 py-3 rounded-xl font-medium text-sm text-indigo-400 border border-indigo-400/30 hover:border-indigo-400/60 hover:bg-indigo-400/5 transition-all duration-200 text-center"
            >
              {t.hero.cta_research}
            </a>
            <button
              onClick={() => scrollToSection('chat', 'chat_with_ai')}
              className="px-6 py-3 rounded-xl font-medium text-sm text-text-primary border border-border hover:border-border-strong hover:bg-white/5 transition-all duration-200"
            >
              {t.hero.cta_chat}
            </button>
          </motion.div>

          {/* Badges */}
          <motion.div variants={itemVariants} className="flex flex-wrap gap-2">
            {[
              { label: t.hero.badge_web3, color: 'cyan' },
              { label: t.hero.badge_quant, color: 'indigo' },
              { label: t.hero.badge_ai, color: 'cyan' },
            ].map((badge) => (
              <span
                key={badge.label}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                  badge.color === 'cyan'
                    ? 'bg-cyan-dim border-cyan-400/20 text-cyan-400'
                    : 'bg-indigo-dim border-indigo-400/20 text-indigo-400'
                }`}
              >
                {badge.label}
              </span>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span className="text-text-muted text-xs font-mono">scroll</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
          className="w-0.5 h-8 bg-gradient-to-b from-text-muted to-transparent rounded-full"
        />
      </motion.div>
    </section>
  )
}
